import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Typography, Box, Paper, TextField, Button, Stack, MenuItem, Menu, ListItemIcon, ListItemText,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
    IconButton, Snackbar, Alert, useMediaQuery, useTheme, InputAdornment, Dialog, DialogContent,
    DialogActions, Divider, Grid, Chip, CircularProgress, Tooltip, Avatar, Tabs, Tab, DialogTitle,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import VideocamIcon from '@mui/icons-material/Videocam';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NotesIcon from '@mui/icons-material/Notes';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import LinkIcon from '@mui/icons-material/Link';
import api from '../services/api';
import { solicitarAutorizacaoSessao } from '../services/autorizacaoOperacao';

const STATUS_CONFIG = {
    AGENDADA: { label: 'Agendada', color: '#0ea5e9', bg: '#e0f2fe' },
    REALIZADA: { label: 'Realizada', color: '#16a34a', bg: '#dcfce7' },
    REALIZADA_ALTERADA: { label: 'Realizada (Alterada)', color: '#7c3aed', bg: '#ede9fe' },
    FALTA: { label: 'Falta', color: '#f59e0b', bg: '#fef3c7' },
    CANCELADA: { label: 'Cancelada', color: '#dc2626', bg: '#fee2e2' },
    CONFIRMADA: { label: 'Confirmada', color: '#059669', bg: '#d1fae5' },
    REAGENDADA: { label: 'Reagendada', color: '#64748b', bg: '#f1f5f9' },
};

const STATUS_FINALIZADOS = ['REALIZADA', 'REALIZADA_ALTERADA'];

/** Estados em que a sessão ainda vai acontecer: dá para confirmar e reagendar. */
const STATUS_ABERTOS = ['AGENDADA', 'CONFIRMADA'];

const TABS = [
    { v: 'AGENDADA', label: 'Agendadas' },
    { v: 'REALIZADA', label: 'Realizadas' },
    { v: 'REALIZADA_ALTERADA', label: 'Alteradas' },
    { v: 'FALTA', label: 'Faltas' },
    { v: 'CANCELADA', label: 'Canceladas' },
    { v: '', label: 'Todas' },
];

const MODALIDADE_LABEL = { PRESENCIAL: 'Presencial', ONLINE: 'Online' };

const SectionCard = ({ icon, title, children, color = '#1ABB9C' }) => (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e5e7eb', bgcolor: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 1.2, bgcolor: `${color}15`, color }}>
                {icon}
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1f2937' }}>{title}</Typography>
        </Box>
        {children}
    </Paper>
);

const InfoField = ({ label, value }) => (
    <Box>
        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', display: 'block', mb: 0.3 }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ color: '#1f2937', wordBreak: 'break-word' }}>{value || '—'}</Typography>
    </Box>
);

export default function Sessoes() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [lista, setLista] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFiltro, setStatusFiltro] = useState('AGENDADA');
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [confirmDel, setConfirmDel] = useState({ open: false, id: null });
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selecionada, setSelecionada] = useState(null);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuSessaoId, setMenuSessaoId] = useState(null);
    const [menuSessaoStatus, setMenuSessaoStatus] = useState(null);
    const [senhaDialog, setSenhaDialog] = useState({ open: false, sessaoId: null, action: null, senha: '', loading: false, error: '' });

    const abrirMenu = (e, idSessao, status) => { setMenuAnchor(e.currentTarget); setMenuSessaoId(idSessao); setMenuSessaoStatus(status); };
    const fecharMenu = () => { setMenuAnchor(null); setMenuSessaoId(null); setMenuSessaoStatus(null); };

    const isFinalizada = (status) => STATUS_FINALIZADOS.includes(status);

    const tentarAcao = (sessaoId, status, action) => {
        if (isFinalizada(status)) {
            setSenhaDialog({ open: true, sessaoId, action, senha: '', loading: false, error: '' });
        } else {
            executarAcao(sessaoId, action);
        }
    };

    const executarAcao = (sessaoId, action) => {
        if (action === 'editar') navigate(`/sessoes/${sessaoId}`);
        else if (action === 'evolucao') navigate(`/sessoes/${sessaoId}/evolucao`);
    };

    const confirmarSenha = async () => {
        setSenhaDialog(s => ({ ...s, loading: true, error: '' }));
        try {
            const action = senhaDialog.action;
            const id = senhaDialog.sessaoId;
            // Recebe autorizacao temporaria de uso unico (fica em memoria);
            // a senha nao e mais persistida no navegador.
            await solicitarAutorizacaoSessao(id, action === 'evolucao' ? 'evolucao' : 'editar', senhaDialog.senha);
            setSenhaDialog({ open: false, sessaoId: null, action: null, senha: '', loading: false, error: '' });
            executarAcao(id, action);
        } catch (e) {
            setSenhaDialog(s => ({ ...s, loading: false, error: e?.response?.data?.message || 'Senha inválida' }));
        }
    };

    // ── Confirmação e reagendamento ──────────────────────────────────────
    const [reagendarDialog, setReagendarDialog] = useState(
        { open: false, id: null, dataHora: '', loading: false, error: '' });

    const recarregarLista = () => carregar(page, rowsPerPage, searchTerm, statusFiltro);

    const confirmarPresenca = async (id) => {
        try {
            await api.patch(`/sessoes/${id}/confirmar`);
            mostrarMensagem('Presença confirmada.');
            recarregarLista();
        } catch (e) {
            mostrarMensagem(e?.response?.data?.message || 'Não foi possível confirmar.', 'error');
        }
    };

    /** Copia o link público para o consultório enviar ao paciente. */
    const copiarLinkConfirmacao = async (id) => {
        try {
            const { data } = await api.post(`/sessoes/${id}/link-confirmacao`);
            const url = `${window.location.origin}${data.caminho}`;
            // clipboard exige contexto seguro; sem ele mostramos a URL para copiar à mão.
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(url);
                mostrarMensagem('Link de confirmação copiado.');
            } else {
                mostrarMensagem(url, 'info');
            }
        } catch (e) {
            mostrarMensagem(e?.response?.data?.message || 'Não foi possível gerar o link.', 'error');
        }
    };

    const abrirReagendar = (id) =>
        setReagendarDialog({ open: true, id, dataHora: '', loading: false, error: '' });

    const confirmarReagendamento = async () => {
        if (!reagendarDialog.dataHora) {
            setReagendarDialog(r => ({ ...r, error: 'Informe a nova data e hora.' }));
            return;
        }
        setReagendarDialog(r => ({ ...r, loading: true, error: '' }));
        try {
            await api.post(`/sessoes/${reagendarDialog.id}/reagendar`,
                { dataHora: reagendarDialog.dataHora });
            setReagendarDialog({ open: false, id: null, dataHora: '', loading: false, error: '' });
            mostrarMensagem('Sessão reagendada. A original fica no histórico como reagendada.');
            recarregarLista();
        } catch (e) {
            setReagendarDialog(r => ({
                ...r, loading: false,
                error: e?.response?.data?.message || 'Não foi possível reagendar.',
            }));
        }
    };

    const debounceRef = useRef(null);
    const mountedRef = useRef(false);

    const carregar = async (pg, rpp, busca, st) => {
        setLoading(true);
        try {
            const params = { page: pg, size: rpp };
            if (busca) params.busca = busca;
            if (st) params.status = st;
            const { data } = await api.get('/sessoes', { params });
            setLista(data.content ?? []);
            setTotalElements(data.totalElements ?? 0);
        } catch {
            mostrarMensagem('Erro ao carregar sessoes', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        mountedRef.current = true;
        carregar(0, rowsPerPage, '', 'AGENDADA');
    }, []);

    useEffect(() => {
        if (!mountedRef.current) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setPage(0);
            carregar(0, rowsPerPage, searchTerm, statusFiltro);
        }, 400);
        return () => clearTimeout(debounceRef.current);
    }, [searchTerm, statusFiltro]);

    useEffect(() => {
        if (!mountedRef.current) return;
        carregar(page, rowsPerPage, searchTerm, statusFiltro);
    }, [page, rowsPerPage]);

    const mostrarMensagem = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

    const formatarDataHora = (iso) => iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';
    const formatarDataCurta = (iso) => iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';

    const handleDelete = async () => {
        try {
            await api.delete(`/sessoes/${confirmDel.id}`);
            mostrarMensagem('Sessao excluida com sucesso');
            setConfirmDel({ open: false, id: null });
            carregar(page, rowsPerPage, searchTerm, statusFiltro);
        } catch (e) {
            mostrarMensagem(e?.response?.data?.message || 'Erro ao excluir sessao', 'error');
        }
    };

    const abrirView = async (id) => {
        try {
            const { data } = await api.get(`/sessoes/${id}`);
            setSelecionada(data);
            setViewModalOpen(true);
        } catch {
            mostrarMensagem('Erro ao carregar sessao', 'error');
        }
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: isMobile ? 'flex-start' : 'center',
                flexDirection: isMobile ? 'column' : 'row',
                gap: 2, mb: 3,
            }}>
                <Box>
                    <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold', color: '#333' }}>
                        Sessões
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                        Total: {totalElements} {totalElements === 1 ? 'sessao' : 'sessoes'}
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/sessoes/novo')}
                    sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' }, px: 3 }}>
                    Nova Sessao
                </Button>
            </Box>

            <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                    <TextField
                        fullWidth size="small" placeholder="Buscar por paciente ou psicólogo..."
                        value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#999' }} /></InputAdornment>,
                            endAdornment: searchTerm && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearchTerm('')}><ClearIcon fontSize="small" /></IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Stack>
            </Paper>

            <Paper elevation={0} sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <Tabs
                    value={statusFiltro}
                    onChange={(_, v) => setStatusFiltro(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
                        '& .Mui-selected': { color: '#1ABB9C !important' },
                        '& .MuiTabs-indicator': { backgroundColor: '#1ABB9C', height: 3 },
                    }}>
                    {TABS.map(t => <Tab key={t.v || 'all'} value={t.v} label={t.label} />)}
                </Tabs>
            </Paper>

            <Paper elevation={0} sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 1050 }}>
                        <TableHead sx={{ bgcolor: '#fafafa' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, width: '26%' }}>Paciente</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: '20%' }}>Psicólogo</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 150 }}>Data / Hora</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 90 }} align="center">Duração</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 130 }}>Modalidade</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 170 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 110 }} align="right">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && (
                                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                    <CircularProgress size={28} sx={{ color: '#1ABB9C' }} />
                                </TableCell></TableRow>
                            )}
                            {!loading && lista.length === 0 && (
                                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: '#888' }}>
                                    Nenhuma sessão encontrada.
                                </TableCell></TableRow>
                            )}
                            {!loading && lista.map(s => {
                                const st = STATUS_CONFIG[s.statusSessao] || { label: s.statusSessao, color: '#666', bg: '#eee' };
                                return (
                                    <TableRow key={s.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                        <TableCell sx={{ overflow: 'hidden' }} title={s.nomePaciente || ''}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
                                                <Avatar sx={{ width: 32, height: 32, bgcolor: '#1ABB9C', fontSize: '0.85rem', fontWeight: 700, flexShrink: 0 }}>
                                                    {(s.nomePaciente || '?').charAt(0).toUpperCase()}
                                                </Avatar>
                                                <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.nomePaciente || '—'}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.nomePsicologo || ''}>{s.nomePsicologo || '—'}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatarDataHora(s.dataHora)}</TableCell>
                                        <TableCell align="center">{s.duracao}min</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={MODALIDADE_LABEL[s.modalidadeSessao] || s.modalidadeSessao}
                                                icon={s.modalidadeSessao === 'ONLINE' ? <VideocamIcon sx={{ fontSize: '1rem' }} /> : undefined}
                                                variant="outlined" />
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small" label={st.label}
                                                sx={{ bgcolor: st.bg, color: st.color, fontWeight: 600 }} />
                                        </TableCell>
                                        <TableCell align="right">
                                            {isFinalizada(s.statusSessao) && (
                                                <Tooltip title="Edição bloqueada — requer senha">
                                                    <LockIcon sx={{ fontSize: '1rem', color: '#dc2626', verticalAlign: 'middle', mr: 0.5 }} />
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Ações">
                                                <IconButton size="small" onClick={(e) => abrirMenu(e, s.id, s.statusSessao)}>
                                                    <MoreVertIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={totalElements}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage="Linhas por pagina:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
            </Paper>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={fecharMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { minWidth: 220, borderRadius: 2 } }}>
                <MenuItem onClick={() => { abrirView(menuSessaoId); fecharMenu(); }}>
                    <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Visualizar</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { tentarAcao(menuSessaoId, menuSessaoStatus, 'editar'); fecharMenu(); }}>
                    <ListItemIcon>{isFinalizada(menuSessaoStatus) ? <LockIcon fontSize="small" sx={{ color: '#dc2626' }} /> : <EditIcon fontSize="small" />}</ListItemIcon>
                    <ListItemText>Editar Dados {isFinalizada(menuSessaoStatus) ? '(requer senha)' : ''}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { tentarAcao(menuSessaoId, menuSessaoStatus, 'evolucao'); fecharMenu(); }}>
                    <ListItemIcon><NotesIcon fontSize="small" sx={{ color: '#1ABB9C' }} /></ListItemIcon>
                    <ListItemText
                        primary="Registrar Evolução"
                        secondary="Anotações + avaliação de risco"
                        primaryTypographyProps={{ fontWeight: 600, color: '#1ABB9C' }}
                        secondaryTypographyProps={{ fontSize: '0.7rem' }}
                    />
                </MenuItem>
                {STATUS_ABERTOS.includes(menuSessaoStatus) && <Divider />}
                {menuSessaoStatus === 'AGENDADA' && (
                    <MenuItem onClick={() => { confirmarPresenca(menuSessaoId); fecharMenu(); }}>
                        <ListItemIcon><EventAvailableIcon fontSize="small" sx={{ color: '#059669' }} /></ListItemIcon>
                        <ListItemText
                            primary="Confirmar presença"
                            secondary="Registra confirmação pelo consultório"
                            secondaryTypographyProps={{ fontSize: '0.7rem' }}
                        />
                    </MenuItem>
                )}
                {STATUS_ABERTOS.includes(menuSessaoStatus) && (
                    <MenuItem onClick={() => { copiarLinkConfirmacao(menuSessaoId); fecharMenu(); }}>
                        <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
                        <ListItemText
                            primary="Copiar link de confirmação"
                            secondary="Para enviar ao paciente"
                            secondaryTypographyProps={{ fontSize: '0.7rem' }}
                        />
                    </MenuItem>
                )}
                {STATUS_ABERTOS.includes(menuSessaoStatus) && (
                    <MenuItem onClick={() => { abrirReagendar(menuSessaoId); fecharMenu(); }}>
                        <ListItemIcon><EventRepeatIcon fontSize="small" sx={{ color: '#0ea5e9' }} /></ListItemIcon>
                        <ListItemText
                            primary="Reagendar"
                            secondary="Cria a nova sessão e registra o vínculo"
                            secondaryTypographyProps={{ fontSize: '0.7rem' }}
                        />
                    </MenuItem>
                )}
                {menuSessaoStatus === 'AGENDADA' && <Divider />}
                {menuSessaoStatus === 'AGENDADA' && (
                    <MenuItem onClick={() => { setConfirmDel({ open: true, id: menuSessaoId }); fecharMenu(); }}>
                        <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                        <ListItemText primaryTypographyProps={{ color: 'error' }}>Excluir</ListItemText>
                    </MenuItem>
                )}
            </Menu>

            <Dialog open={viewModalOpen} onClose={() => setViewModalOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
                {selecionada && (
                    <>
                        <Box sx={{
                            background: 'linear-gradient(135deg, #1ABB9C 0%, #0e8a72 100%)',
                            px: { xs: 2.5, sm: 3.5 }, pt: 3, pb: 3,
                            display: 'flex', alignItems: 'flex-start', gap: 2,
                        }}>
                            <Avatar sx={{ width: 56, height: 56, bgcolor: 'rgba(255,255,255,0.18)', color: 'white', fontSize: '1.5rem', fontWeight: 700, border: '3px solid rgba(255,255,255,0.35)' }}>
                                {(selecionada.nomePaciente || '?').charAt(0).toUpperCase()}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="h6" fontWeight={700} color="white" sx={{ lineHeight: 1.2, mb: 1 }}>
                                    {selecionada.nomePaciente}
                                </Typography>
                                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                    <Chip size="small" label={formatarDataHora(selecionada.dataHora)} sx={{ bgcolor: 'rgba(255,255,255,0.22)', color: 'white', fontWeight: 600 }} />
                                    <Chip size="small" label={MODALIDADE_LABEL[selecionada.modalidadeSessao]} sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.88)' }} />
                                </Stack>
                            </Box>
                            <IconButton onClick={() => setViewModalOpen(false)} size="small"
                                sx={{ color: 'rgba(255,255,255,0.85)' }}><CloseIcon /></IconButton>
                        </Box>

                        <DialogContent sx={{ p: 3, bgcolor: '#f4f6f8' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <SectionCard icon={<EventAvailableIcon sx={{ fontSize: 19 }} />} title="Dados da Sessao">
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, sm: 6 }}><InfoField label="Data e Hora" value={formatarDataHora(selecionada.dataHora)} /></Grid>
                                        <Grid size={{ xs: 6, sm: 3 }}><InfoField label="Duracao" value={`${selecionada.duracao} min`} /></Grid>
                                        <Grid size={{ xs: 6, sm: 3 }}><InfoField label="Modalidade" value={MODALIDADE_LABEL[selecionada.modalidadeSessao]} /></Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}><InfoField label="Paciente" value={selecionada.nomePaciente} /></Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}><InfoField label="Psicologo" value={selecionada.nomePsicologo} /></Grid>
                                        <Grid size={12}>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', display: 'block', mb: 0.5 }}>Status</Typography>
                                            <Chip size="small" label={STATUS_CONFIG[selecionada.statusSessao]?.label || selecionada.statusSessao}
                                                sx={{ bgcolor: STATUS_CONFIG[selecionada.statusSessao]?.bg, color: STATUS_CONFIG[selecionada.statusSessao]?.color, fontWeight: 600 }} />
                                        </Grid>
                                    </Grid>
                                </SectionCard>

                                {selecionada.modalidadeSessao === 'ONLINE' && selecionada.linkVideo && (
                                    <SectionCard icon={<VideocamIcon sx={{ fontSize: 19 }} />} title="Link de Video" color="#6366f1">
                                        <Button variant="outlined" href={selecionada.linkVideo} target="_blank" rel="noopener" startIcon={<VideocamIcon />}>
                                            Abrir reuniao
                                        </Button>
                                    </SectionCard>
                                )}
                            </Box>
                        </DialogContent>

                        <DialogActions sx={{ px: 3, py: 2, bgcolor: 'white', borderTop: '1px solid #eee', gap: 1 }}>
                            <Button onClick={() => setViewModalOpen(false)} variant="outlined" color="inherit">Fechar</Button>
                            <Button onClick={() => { setViewModalOpen(false); navigate(`/sessoes/${selecionada.id}`); }}
                                variant="contained" startIcon={<EditIcon />}
                                sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' } }}>Editar</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <Dialog open={confirmDel.open} onClose={() => setConfirmDel({ open: false, id: null })}>
                <Box sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>Excluir sessao?</Typography>
                    <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>Esta acao nao pode ser desfeita.</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button onClick={() => setConfirmDel({ open: false, id: null })}>Cancelar</Button>
                        <Button color="error" variant="contained" onClick={handleDelete}>Excluir</Button>
                    </Stack>
                </Box>
            </Dialog>

            <Dialog open={senhaDialog.open} onClose={() => setSenhaDialog({ open: false, sessaoId: null, action: null, senha: '', loading: false, error: '' })} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LockIcon sx={{ color: '#dc2626' }} /> Sessão finalizada
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                        Confirme sua senha para liberar a edição. A sessão será marcada como "Realizada (Alterada)".
                    </Typography>
                    <TextField fullWidth type="password" label="Sua senha" autoFocus
                        value={senhaDialog.senha}
                        onChange={(e) => setSenhaDialog(s => ({ ...s, senha: e.target.value, error: '' }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') confirmarSenha(); }}
                        error={!!senhaDialog.error}
                        helperText={senhaDialog.error} />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setSenhaDialog({ open: false, sessaoId: null, action: null, senha: '', loading: false, error: '' })}>Cancelar</Button>
                    <Button variant="contained" onClick={confirmarSenha} disabled={senhaDialog.loading || !senhaDialog.senha}
                        sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' } }}>
                        {senhaDialog.loading ? <CircularProgress size={18} color="inherit" /> : 'Confirmar'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={reagendarDialog.open}
                onClose={() => setReagendarDialog(r => ({ ...r, open: false }))}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Reagendar sessão</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        A sessão atual fica registrada como <strong>reagendada</strong> e a nova nasce
                        agendada, com o vínculo entre as duas. O crédito do plano não é consumido duas vezes.
                    </Typography>
                    <TextField
                        type="datetime-local"
                        label="Nova data e hora"
                        fullWidth
                        value={reagendarDialog.dataHora}
                        onChange={(e) => setReagendarDialog(r => ({ ...r, dataHora: e.target.value, error: '' }))}
                        InputLabelProps={{ shrink: true }}
                        error={!!reagendarDialog.error}
                        helperText={reagendarDialog.error || ' '}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setReagendarDialog(r => ({ ...r, open: false }))}>Cancelar</Button>
                    <Button
                        variant="contained"
                        loading={reagendarDialog.loading}
                        onClick={confirmarReagendamento}
                        sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' } }}
                    >
                        Reagendar
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
